import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Lock,
    CheckCircle2,
    Trophy,
    Info
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from './dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Badge configurations
const badgeConfig = {
    basic: {
        name: 'NUSATRADE BASIC',
        name_id: 'NUSATRADE BASIC',
        description: 'Foundation badge - Complete all Basic level modules',
        description_id: 'Lencana Asas - Lengkapkan semua modul level Basic',
        image: '/badges/badge_basic.png',
        feeling: 'Foundation, discipline, first milestone',
        feeling_id: 'Asas, disiplin, pencapaian pertama',
        color: 'red',
        bgGradient: 'from-red-500 to-red-600',
        requirements: {
            level: 'Basic',
            moduleComplete: 100,
            checklistConsistent: true,
            journalConsistent: true
        }
    },
    intermediate: {
        name: 'NUSATRADE INTERMEDIATE',
        name_id: 'NUSATRADE INTERMEDIATE',
        description: 'Control badge - Complete all Intermediate level modules',
        description_id: 'Lencana Kawalan - Lengkapkan semua modul level Intermediate',
        image: '/badges/badge_intermediate.png',
        feeling: 'Control, structure, improving consistency',
        feeling_id: 'Kawalan, struktur, konsistensi meningkat',
        color: 'slate',
        bgGradient: 'from-slate-500 to-slate-600',
        requirements: {
            level: 'Intermediate',
            moduleComplete: 100,
            checklistConsistent: true,
            journalConsistent: true
        }
    },
    advance: {
        name: 'NUSATRADE ADVANCE',
        name_id: 'NUSATRADE ADVANCE',
        description: 'Precision badge - Complete all Advanced level modules',
        description_id: 'Lencana Ketepatan - Lengkapkan semua modul level Advanced',
        image: '/badges/badge_advance.png',
        feeling: 'Precision, confidence, professional trader',
        feeling_id: 'Ketepatan, keyakinan, trader profesional',
        color: 'amber',
        bgGradient: 'from-amber-500 to-amber-600',
        requirements: {
            level: 'Advanced',
            moduleComplete: 100,
            checklistConsistent: true,
            journalConsistent: true
        }
    },
    expert: {
        name: 'NUSATRADE EXPERT',
        name_id: 'NUSATRADE EXPERT',
        description: 'Mastery badge - Complete all Expert level modules',
        description_id: 'Lencana Penguasaan - Lengkapkan semua modul level Expert',
        image: '/badges/badge_expert.png',
        feeling: 'Mastery, authority, elite trader status',
        feeling_id: 'Penguasaan, autoriti, status trader elit',
        color: 'yellow',
        bgGradient: 'from-yellow-500 to-yellow-600',
        requirements: {
            level: 'Expert',
            moduleComplete: 100,
            checklistConsistent: true,
            journalConsistent: true
        }
    }
};

export const AchievementBadges = ({ className, showAll = false }) => {
    const { language } = useLanguage();
    const [earnedBadges, setEarnedBadges] = useState([]);
    const [levelProgress, setLevelProgress] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedBadge, setSelectedBadge] = useState(null);

    useEffect(() => {
        fetchBadgeData();
    }, []);

    const fetchBadgeData = async () => {
        try {
            // Fetch user progress and badges
            const [progressRes, badgesRes] = await Promise.all([
                axios.get(`${API}/progress`),
                axios.get(`${API}/badges/earned`)
            ]);

            // Calculate level progress
            const progress = {};
            if (progressRes.data?.levels) {
                progressRes.data.levels.forEach(level => {
                    progress[level.name.toLowerCase()] = level.progress;
                });
            }
            setLevelProgress(progress);

            // Set earned badges
            setEarnedBadges(badgesRes.data.map(b => b.level?.toLowerCase()));
        } catch (error) {
            console.error('Error fetching badge data:', error);
            setEarnedBadges([]);
        } finally {
            setLoading(false);
        }
    };

    const getBadgeStatus = (badgeKey) => {
        const isEarned = earnedBadges.includes(badgeKey);
        const progress = levelProgress[badgeKey] || 0;
        
        return {
            isEarned,
            progress,
            isLocked: !isEarned && progress < 100
        };
    };

    const handleBadgeClick = (badgeKey) => {
        setSelectedBadge(badgeKey);
    };

    if (loading) {
        return (
            <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-4", className)}>
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className="animate-pulse">
                        <div className="aspect-square bg-slate-200 rounded-2xl"></div>
                    </div>
                ))}
            </div>
        );
    }

    const badges = Object.entries(badgeConfig);
    const displayBadges = showAll ? badges : badges.filter(([key]) => {
        const status = getBadgeStatus(key);
        return status.isEarned || status.progress > 0;
    });

    // If no badges to show yet
    if (!showAll && displayBadges.length === 0) {
        return (
            <div className={cn("glass-card rounded-2xl p-6 text-center", className)}>
                <Trophy className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-700 mb-2">
                    {language === 'id' ? 'Belum Ada Badge' : 'No Badges Yet'}
                </h3>
                <p className="text-sm text-slate-500">
                    {language === 'id' 
                        ? 'Lengkapkan modul untuk unlock badge pertama anda'
                        : 'Complete modules to unlock your first badge'
                    }
                </p>
            </div>
        );
    }

    return (
        <>
            <div className={cn("space-y-4", className)} data-testid="achievement-badges">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        <h3 className="font-heading font-semibold text-slate-900">
                            {language === 'id' ? 'Achievement Badges' : 'Achievement Badges'}
                        </h3>
                    </div>
                    <span className="text-sm text-slate-500">
                        {earnedBadges.length}/4 {language === 'id' ? 'diperoleh' : 'earned'}
                    </span>
                </div>

                {/* Badges Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {(showAll ? badges : badges).map(([key, badge]) => {
                        const status = getBadgeStatus(key);
                        
                        return (
                            <button
                                key={key}
                                onClick={() => handleBadgeClick(key)}
                                className={cn(
                                    "relative group rounded-2xl overflow-hidden transition-all duration-300",
                                    status.isEarned 
                                        ? "hover:scale-105 hover:shadow-xl" 
                                        : "opacity-60 hover:opacity-80"
                                )}
                                data-testid={`badge-${key}`}
                            >
                                {/* Badge Image Container */}
                                <div className={cn(
                                    "aspect-square rounded-2xl overflow-hidden",
                                    !status.isEarned && "grayscale"
                                )}>
                                    <img
                                        src={badge.image}
                                        alt={badge.name}
                                        className="w-full h-full object-cover"
                                    />
                                    
                                    {/* Locked Overlay */}
                                    {!status.isEarned && (
                                        <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center">
                                            <Lock className="w-8 h-8 text-white/80 mb-2" />
                                            <span className="text-xs text-white/80 font-medium">
                                                {status.progress}%
                                            </span>
                                        </div>
                                    )}

                                    {/* Earned Indicator */}
                                    {status.isEarned && (
                                        <div className="absolute top-2 right-2 bg-emerald-500 rounded-full p-1">
                                            <CheckCircle2 className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Badge Name */}
                                <div className="mt-2 text-center">
                                    <p className={cn(
                                        "text-xs font-medium truncate",
                                        status.isEarned ? "text-slate-700" : "text-slate-400"
                                    )}>
                                        {badge.requirements.level}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Info Note */}
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                        {language === 'id'
                            ? 'Badge unlock bila 100% modul selesai + checklist & journal konsisten'
                            : 'Badge unlocks when 100% module complete + consistent checklist & journal'
                        }
                    </p>
                </div>
            </div>

            {/* Badge Detail Dialog */}
            <Dialog open={!!selectedBadge} onOpenChange={() => setSelectedBadge(null)}>
                <DialogContent className="max-w-md">
                    {selectedBadge && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-center">
                                    {badgeConfig[selectedBadge].name}
                                </DialogTitle>
                                <DialogDescription className="text-center">
                                    {language === 'id' 
                                        ? badgeConfig[selectedBadge].feeling_id 
                                        : badgeConfig[selectedBadge].feeling
                                    }
                                </DialogDescription>
                            </DialogHeader>
                            
                            <div className="flex flex-col items-center py-4">
                                <div className={cn(
                                    "w-48 h-48 rounded-2xl overflow-hidden mb-4",
                                    !getBadgeStatus(selectedBadge).isEarned && "grayscale opacity-50"
                                )}>
                                    <img
                                        src={badgeConfig[selectedBadge].image}
                                        alt={badgeConfig[selectedBadge].name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                
                                <p className="text-sm text-slate-600 text-center mb-4">
                                    {language === 'id' 
                                        ? badgeConfig[selectedBadge].description_id 
                                        : badgeConfig[selectedBadge].description
                                    }
                                </p>

                                {/* Requirements */}
                                <div className="w-full space-y-2">
                                    <h4 className="text-sm font-medium text-slate-700">
                                        {language === 'id' ? 'Syarat Unlock:' : 'Unlock Requirements:'}
                                    </h4>
                                    <ul className="text-sm text-slate-600 space-y-1">
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className={cn(
                                                "w-4 h-4",
                                                (levelProgress[selectedBadge] || 0) >= 100 
                                                    ? "text-emerald-500" 
                                                    : "text-slate-300"
                                            )} />
                                            100% {badgeConfig[selectedBadge].requirements.level} {language === 'id' ? 'selesai' : 'complete'}
                                            <span className="text-slate-400 ml-auto">
                                                {levelProgress[selectedBadge] || 0}%
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-slate-300" />
                                            {language === 'id' ? 'Checklist konsisten' : 'Consistent checklist'}
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-slate-300" />
                                            {language === 'id' ? 'Journal konsisten' : 'Consistent journal'}
                                        </li>
                                    </ul>
                                </div>

                                {/* Status */}
                                <div className={cn(
                                    "mt-4 px-4 py-2 rounded-full text-sm font-medium",
                                    getBadgeStatus(selectedBadge).isEarned
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-slate-100 text-slate-600"
                                )}>
                                    {getBadgeStatus(selectedBadge).isEarned
                                        ? (language === 'id' ? '✓ Badge Diperoleh!' : '✓ Badge Earned!')
                                        : (language === 'id' ? '🔒 Belum Unlock' : '🔒 Not Unlocked')
                                    }
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
